import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, differenceInSeconds } from 'date-fns';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMapPin, FiClock, FiLogIn, FiLogOut, FiCalendar, FiActivity, FiUsers, FiUser } = FiIcons;

const AttendanceTracker = () => {
  const { activeAttendance, clockIn, clockOut, attendance, teamMembers } = useData();
  const { user, hasPermission } = useAuth();
  const [elapsed, setElapsed] = useState(0);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');

  const isAdmin = hasPermission('view_team') || hasPermission('all');

  useEffect(() => {
    let interval;
    if (activeAttendance) {
      interval = setInterval(() => {
        setElapsed(differenceInSeconds(new Date(), new Date(activeAttendance.startTime)));
      }, 1000);
    } else {
      setElapsed(0);
    }
    return () => clearInterval(interval);
  }, [activeAttendance]);

  const handleClockIn = async () => {
    setLoading(true);
    const result = await clockIn(note);
    if (result.success) setNote('');
    else alert(result.error);
    setLoading(false);
  };

  const handleClockOut = async () => {
    setLoading(true);
    const result = await clockOut();
    if (!result.success) alert(result.error);
    setLoading(false);
  };

  const formatDuration = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  // Filter attendance for personal view
  const myLogs = attendance.filter(log => log.userId === user.id);
  const teamLogs = attendance;

  return (
    <div className="space-y-6">
      {/* Tab Selection (Admin Only) */}
      {isAdmin && (
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
          <button 
            onClick={() => setActiveTab('personal')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${activeTab === 'personal' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            My Attendance
          </button>
          <button 
            onClick={() => setActiveTab('team')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${activeTab === 'team' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Team Presence
          </button>
        </div>
      )}

      <AnimatePresence mode="wait">
        {activeTab === 'personal' ? (
          <motion.div key="personal" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Clock Card */}
              <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                    <SafeIcon icon={FiActivity} className="w-5 h-5 mr-2 text-blue-600" /> Current Session
                  </h2>
                  {activeAttendance && (
                    <span className="flex items-center text-green-600 text-sm font-medium animate-pulse">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span> Active
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="flex flex-col justify-center text-center md:text-left">
                    <p className="text-sm text-gray-500 uppercase tracking-wider font-semibold">Time Elapsed</p>
                    <h3 className="text-5xl font-mono font-bold text-gray-900 mt-2">
                      {activeAttendance ? formatDuration(elapsed) : "00:00:00"}
                    </h3>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                    {!activeAttendance ? (
                      <div className="space-y-4">
                        <input 
                          type="text" 
                          value={note} 
                          onChange={(e) => setNote(e.target.value)} 
                          placeholder="Note..." 
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white" 
                        />
                        <button 
                          onClick={handleClockIn} 
                          disabled={loading}
                          className="w-full flex items-center justify-center space-x-2 bg-blue-600 text-white px-6 py-4 rounded-xl hover:bg-blue-700 transition-all font-bold disabled:opacity-50"
                        >
                          {loading ? <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : <><SafeIcon icon={FiLogIn} className="w-5 h-5" /><span>Clock In</span></>}
                        </button>
                      </div>
                    ) : (
                      <button 
                        onClick={handleClockOut} 
                        disabled={loading}
                        className="w-full flex items-center justify-center space-x-2 bg-red-600 text-white px-6 py-4 rounded-xl hover:bg-red-700 transition-all font-bold disabled:opacity-50"
                      >
                        {loading ? <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : <><SafeIcon icon={FiLogOut} className="w-5 h-5" /><span>Clock Out</span></>}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Summary Stats */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Today's Summary</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">Total Shifts</span>
                    <span className="font-bold text-gray-900">{myLogs.length}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">First In</span>
                    <span className="font-bold text-gray-900">
                      {myLogs.length > 0 ? format(new Date(myLogs[myLogs.length-1].startTime), 'hh:mm a') : '--:--'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* My History Table */}
            <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <h3 className="font-semibold text-gray-900 flex items-center">
                  <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-2" /> My Recent Logs
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
                    <tr>
                      <th className="px-6 py-3">Date</th>
                      <th className="px-6 py-3">Clock In</th>
                      <th className="px-6 py-3">Clock Out</th>
                      <th className="px-6 py-3">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {myLogs.filter(l => l.status === 'completed').slice(0, 5).map(log => {
                      const start = new Date(log.startTime);
                      const end = new Date(log.endTime);
                      return (
                        <tr key={log.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">{format(start, 'MMM dd, yyyy')}</td>
                          <td className="px-6 py-4 text-gray-600">{format(start, 'hh:mm a')}</td>
                          <td className="px-6 py-4 text-gray-600">{format(end, 'hh:mm a')}</td>
                          <td className="px-6 py-4 text-blue-600 font-semibold">{formatDuration(differenceInSeconds(end, start))}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div key="team" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 flex items-center">
                  <SafeIcon icon={FiUsers} className="w-4 h-4 mr-2 text-blue-600" /> Team Attendance Monitor
                </h3>
                <span className="text-xs text-gray-500">Live Status</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
                    <tr>
                      <th className="px-6 py-4">Employee</th>
                      <th className="px-6 py-4">Status</th>
                      <th className="px-6 py-4">Clock In</th>
                      <th className="px-6 py-4">Clock Out</th>
                      <th className="px-6 py-4">Duration</th>
                      <th className="px-6 py-4">Device</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {teamLogs.map(log => {
                      const member = teamMembers.find(m => m.id === log.userId);
                      const start = new Date(log.startTime);
                      const end = log.endTime ? new Date(log.endTime) : null;
                      const isActive = log.status === 'active';
                      
                      return (
                        <tr key={log.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-3">
                              <img src={member?.avatar} alt="" className="w-8 h-8 rounded-full bg-gray-100" />
                              <div>
                                <p className="font-medium text-gray-900">{member?.name || log.userName || 'Unknown'}</p>
                                <p className="text-xs text-gray-500">{member?.department}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${isActive ? 'bg-green-100 text-green-700 animate-pulse' : 'bg-gray-100 text-gray-600'}`}>
                              {isActive ? 'Clocked In' : 'Clocked Out'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-gray-600">{format(start, 'MMM dd, hh:mm a')}</td>
                          <td className="px-6 py-4 text-gray-600">{end ? format(end, 'hh:mm a') : '--:--'}</td>
                          <td className="px-6 py-4 font-semibold text-blue-600">
                            {isActive ? formatDuration(differenceInSeconds(new Date(), start)) : formatDuration(differenceInSeconds(end, start))}
                          </td>
                          <td className="px-6 py-4 text-gray-400 text-xs">{log.device}</td>
                        </tr>
                      );
                    })}
                    {teamLogs.length === 0 && (
                      <tr><td colSpan="6" className="px-6 py-10 text-center text-gray-500">No attendance records found.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AttendanceTracker;